import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Socket } from 'ngx-socket-io';
import { concat, merge, Observable, of, timer } from 'rxjs';
import { auditTime, catchError, map, startWith, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface AttentionWarehouse { warehouseId: number; warehouseName: string; count: number; }
export interface ProcurementAttentionSummary {
  needsOrder: { count: number; warehouses: AttentionWarehouse[] };
  minimumStock: { count: number; warehouses: AttentionWarehouse[] };
  overdueDeliveries: { count: number; warehouses: AttentionWarehouse[] };
  postOfficeReady: { count: number; warehouses: AttentionWarehouse[] };
  asOf: string;
}
export interface ProcurementAttentionState {
  summary: ProcurementAttentionSummary | null;
  unavailable: boolean;
}

@Injectable({ providedIn: 'root' })
export class ProcurementAttentionService {
  readonly changes$ = merge(
    this.socket.fromEvent('procurement_attention_changed'), this.socket.fromEvent('connect')
  );

  constructor(private readonly http: HttpClient, private readonly socket: Socket) {}

  watch(): Observable<ProcurementAttentionState> {
    return merge(
      of('refresh'), this.changes$.pipe(map(() => 'refresh')),
      this.socket.fromEvent('disconnect').pipe(map(() => 'offline'))
    ).pipe(
      auditTime(150),
      switchMap(event => event === 'offline'
        ? of({ summary: null, unavailable: true })
        : this.read()),
      startWith({ summary: null, unavailable: false })
    );
  }
  private read(attempt = 0): Observable<ProcurementAttentionState> {
    return this.http.get<ProcurementAttentionSummary>(`${environment.BACKEND}/procurement-attention/summary`).pipe(
      map(summary => ({ summary, unavailable: false })),
      // Retry only failed reads. Healthy counters refresh exclusively through socket events.
      // Disconnect, logout or a newer event cancels both this timer and its HTTP request.
      catchError(() => concat(
        of({ summary: null, unavailable: true }),
        timer(Math.min(5000 * Math.pow(2, attempt), 60000)).pipe(switchMap(() => this.read(attempt + 1)))
      ))
    );
  }
}
